/* Oricle find-your-fit quiz, ported 9/29 from the design's QuizStage (components/marketing/QuizStage.jsx,
   content/quiz-brief-9-28.md). Intro, who is this for, six counted questions, then the result. No answer ends the
   quiz early (Jackson, 10/10). The script holds no copy: every string, weight and reason is on the section's markup
   (sections/oricle-quiz.liquid). Each answer adds question weight x answer weight per model; ties break to the first
   id in the tie order (the cheapest); the runner-up shows at >= the threshold share of the pick's score.
   The versioned storage key prevents answers to an older question deck from being reinterpreted; within a version,
   an answer's data-replaces names retired ids that restore as it (the 10/8 "severe" exit is now "moderate_plus").
   Answers persist in sessionStorage for the tab (Start and Retake clear them). The first name typed for "Someone I'm
   helping" stays in this object's memory: never stored, sent, put in a URL or passed to analytics. Events carry ids
   only: an `oricle:quiz` DOM event and, when present, Shopify.analytics.publish for Customer Events pixels. */
(() => {
  'use strict';

  /* == Pure part: parsing and scoring. No DOM, no copy (exposed as window.Oricle.quiz for tests). == */
  const pairs = (str) => String(str || '').split(',').map((p) => {
    const i = p.indexOf(':');
    return i < 0 ? null : [p.slice(0, i).trim(), p.slice(i + 1).trim()];
  }).filter((x) => x && x[0] && x[1]);
  // "2.0:3, pro:1, truefit:1" → { '2.0': 3, pro: 1, truefit: 1 }
  const parseWeights = (str) => { const o = {}; pairs(str).forEach(([k, v]) => { const n = parseFloat(v); if (!Number.isNaN(n)) o[k] = n; }); return o; };
  // "pro:noise, truefit:noise" → { pro: 'noise', truefit: 'noise' }
  const parseKeys = (str) => { const o = {}; pairs(str).forEach(([k, v]) => { o[k] = v; }); return o; };
  // One "key: sentence" per line; the sentence may itself hold a colon.
  const parseReasons = (str) => {
    const o = {};
    String(str || '').split(/\r?\n/).forEach((line) => {
      const i = line.indexOf(':');
      if (i < 1) return;
      const k = line.slice(0, i).trim(); const t = line.slice(i + 1).trim();
      if (k && t) o[k] = t;
    });
    return o;
  };
  const list = (str) => String(str || '').split(',').map((x) => x.trim()).filter(Boolean);

  /* questions: [{ id, qw, a: [{ id, w, r, firstPair }] }] · models: [{ id, reasons, fill }] (brief §3.4). */
  function score(answers, questions, models, tieOrder, runnerUpMin) {
    const pick = (q) => q.a.find((a) => a.id === answers[q.id]) || null;
    const s = {};
    models.forEach((mo) => { s[mo.id] = 0; });
    questions.forEach((q) => {
      const a = pick(q);
      if (a && a.w && q.qw) Object.keys(a.w).forEach((id) => { if (id in s) s[id] += q.qw * a.w[id]; });
    });
    const rank = (id) => { const i = tieOrder.indexOf(id); return i < 0 ? 99 : i; };
    const order = models.map((mo) => mo.id).sort((x, y) => s[y] - s[x] || rank(x) - rank(y));
    const pid = order[0]; const sid = order[1];
    const runnerUp = sid && s[pid] > 0 && s[sid] >= s[pid] * runnerUpMin ? sid : null;
    const mo = models.find((x) => x.id === pid);
    const keys = []; const said = [];
    questions.forEach((q) => {
      const a = pick(q); const k = a && a.r && a.r[pid];
      if (k && keys.indexOf(k) < 0 && mo.reasons[k]) { keys.push(k); said.push({ q: q.id, a: a.id }); }
    });
    (mo.fill || []).forEach((k) => { if (keys.length < 2 && keys.indexOf(k) < 0 && mo.reasons[k]) { keys.push(k); said.push(null); } });
    const ru = runnerUp && models.find((x) => x.id === runnerUp);
    let runnerUpReason = null;
    if (ru) {
      const k = questions.map((q) => { const a = pick(q); return a && a.r && a.r[runnerUp]; }).filter((x) => x && ru.reasons[x])[0]
        || (ru.fill || []).find((x) => ru.reasons[x]);
      runnerUpReason = k ? ru.reasons[k] : null;
    }
    const firstPair = questions.some((q) => { const a = pick(q); return a && a.firstPair; });
    return { scores: s, primary: pid, runnerUp, reasons: keys.slice(0, 3).map((k, i) => ({ key: k, text: mo.reasons[k], said: said[i] })), firstPair, runnerUpReason };
  }

  window.Oricle = Object.assign(window.Oricle || {}, { quiz: { score, parseWeights, parseKeys, parseReasons, list } });
  if (typeof HTMLElement === 'undefined' || typeof customElements === 'undefined') return;

  /* == The stage == */
  const fill = (t, v) => String(t || '').replace(/\{(\w+)\}/g, (m, k) => (v && v[k] != null ? v[k] : m));
  const reduce = () => !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  const desktop = () => !window.matchMedia || window.matchMedia('(min-width: 768px)').matches;
  const VIEWS = ['intro', 'q', 'result'];

  class OricleQuiz extends HTMLElement {
    connectedCallback() {
      if (!this._bound) this.init();
      document.addEventListener('keydown', this.onKey);
      document.addEventListener('shopify:block:select', this.onBlock);
    }
    disconnectedCallback() {
      document.removeEventListener('keydown', this.onKey);
      document.removeEventListener('shopify:block:select', this.onBlock);
      clearTimeout(this.timer);
    }

    init() {
      this._bound = true;
      const d = this.dataset;
      this.storageKey = d.storage || 'oricle-quiz';
      this.tie = list(d.tie);
      this.min = parseFloat(d.min);
      if (Number.isNaN(this.min)) this.min = 0.5;
      this.views = {};
      this.querySelectorAll('[data-o-qz-view]').forEach((v) => { this.views[v.dataset.oQzView] = v; });
      this.live = this.querySelector('[data-o-qz-live]');
      this.check = this.querySelector('template[data-o-qz-check]');
      this.questions = [...this.querySelectorAll('[data-o-qz-q]')].map((el) => ({
        el, id: el.dataset.key, qw: parseFloat(el.dataset.qw) || 0, counted: el.dataset.counted !== 'false',
        a: [...el.querySelectorAll('[data-o-qz-opt]')].map((b) => ({
          el: b, id: b.dataset.key, w: parseWeights(b.dataset.w), r: parseKeys(b.dataset.r), was: list(b.dataset.replaces),
          firstPair: b.hasAttribute('data-first'), care: b.hasAttribute('data-care')
        }))
      }));
      this.counted = this.questions.filter((q) => q.counted);
      this.results = [...this.querySelectorAll('[data-o-qz-res]')];
      this.models = this.results.map((el) => ({ id: el.dataset.model, reasons: parseReasons(el.dataset.reasons), fill: list(el.dataset.fill) }));
      this.rows = [...this.querySelectorAll('[data-o-qz-row]')];
      this.also = this.querySelector('[data-o-qz-also]');
      this.progress = this.querySelector('[data-o-qz-progress]');
      this.progressLabel = this.querySelector('[data-o-qz-progress-label]');
      this.segs = [...this.querySelectorAll('.o-qz-progress__seg')];
      this.name = '';
      this.pending = null;
      this.user = false;
      this.st = this.restore();

      // The script is here: the quiz takes over from the no-JS compare action.
      this.querySelectorAll('[data-o-qz-start],[data-o-qz-js]').forEach((el) => { el.hidden = false; });
      this.querySelectorAll('[data-o-qz-fallback]').forEach((el) => { el.hidden = true; });

      this.onKey = this.onKey.bind(this);
      this.onBlock = this.onBlock.bind(this);
      this.addEventListener('click', (e) => this.onClick(e));
      this.addEventListener('submit', (e) => this.onSubmit(e));
      this.addEventListener('input', (e) => { if (e.target.matches('[data-o-qz-name-field]')) this.name = e.target.value; });
      this.addEventListener('keydown', (e) => { if (e.key === 'Enter' && e.target.matches('[data-o-qz-name-field]')) { e.preventDefault(); this.next(); } });
      this.render(false);
    }

    /* A refresh resumes the answers; a retired id restores as the answer that replaces it, and anything else that no
       longer matches the deck is dropped. The name is never read back. */
    restore() {
      const blank = { view: 'intro', step: 0, answers: {} };
      try {
        const s = JSON.parse(sessionStorage.getItem(this.storageKey));
        if (!s || typeof s !== 'object' || !this.questions.length) return blank;
        const answers = {}; let moved = -1;
        if (s.answers) this.questions.forEach((q, i) => {
          const v = s.answers[q.id]; if (typeof v !== 'string') return;
          if (q.a.some((a) => a.id === v)) { answers[q.id] = v; return; }
          const a = q.a.find((x) => x.was.indexOf(v) >= 0);
          if (a) { answers[q.id] = a.id; if (moved < 0) moved = i; }
        });
        const missing = this.questions.findIndex((q) => !answers[q.id]);
        const step = Math.min(Math.max(parseInt(s.step, 10) || 0, 0), this.questions.length - 1);
        // The retired audiologist exit (10/8 deck) resumes on the question it ended, its answer replaced and checked.
        if (s.view === 'exit') return { view: 'q', step: moved >= 0 ? moved : missing >= 0 ? missing : step, answers };
        // A stale/deleted answer must never restore a completed recommendation or skip a question.
        if (s.view === 'result' && missing >= 0) return { view: 'q', step: missing, answers };
        return { view: VIEWS.indexOf(s.view) >= 0 ? s.view : 'intro', step: missing >= 0 ? Math.min(step, missing) : step, answers };
      } catch (e) { return blank; }
    }
    save() {
      try { sessionStorage.setItem(this.storageKey, JSON.stringify({ view: this.st.view, step: this.st.step, answers: this.st.answers })); } catch (e) { /* private mode */ }
    }
    clear() { try { sessionStorage.removeItem(this.storageKey); } catch (e) { /* private mode */ } }

    go(patch) {
      clearTimeout(this.timer);
      this.user = true;
      this.pending = null;
      this.force = null;
      Object.assign(this.st, patch);
      this.save();
      this.render(true);
    }
    start() { this.clear(); this.name = ''; this.emit('quiz_start'); this.go({ view: 'q', step: 0, answers: {} }); }
    retake() { this.clear(); this.name = ''; this.go({ view: 'intro', step: 0, answers: {} }); }
    next() { if (this.st.view !== 'q') return; if (this.st.step + 1 >= this.questions.length) this.go({ view: 'result' }); else this.go({ step: this.st.step + 1 }); }
    back() {
      const st = this.st;
      if (st.view === 'result') this.go({ view: 'q', step: this.questions.length - 1 });
      else if (st.step === 0) this.go({ view: 'intro' });
      else this.go({ step: st.step - 1 });
    }

    choose(aid) {
      if (this.pending || this.st.view !== 'q') return;
      const q = this.questions[this.st.step];
      const a = q && q.a.find((x) => x.id === aid);
      if (!a) return;
      this.user = true;
      this.st.answers = Object.assign({}, this.st.answers, { [q.id]: aid });
      this.emit('quiz_answer', { question: q.id, answer: aid });
      this.save();
      if (a.care) { this.render(false); return; } // reveals the optional name field; Continue advances
      this.pending = aid;
      this.render(false);
      this.timer = setTimeout(() => {
        this.pending = null;
        if (this.st.step + 1 >= this.questions.length) this.st.view = 'result'; else this.st.step += 1;
        this.save();
        this.render(true);
      }, reduce() ? 120 : 260);
    }

    isCare() { return this.questions.some((q) => q.a.some((a) => a.care && this.st.answers[q.id] === a.id)); }
    label(a) {
      const b = a.el.dataset;
      return this.isCare() && b.labelCare ? b.labelCare : b.label;
    }

    /* == Rendering == */
    render(move) {
      const st = this.st;
      if (st.view !== 'intro' && !this.questions.length) st.view = 'intro';
      const sig = st.view + (st.view === 'q' ? ':' + st.step : '') + (this.force ? ':' + this.force : '');
      const changed = sig !== this.shown;
      this.shown = sig;
      Object.keys(this.views).forEach((k) => { this.views[k].hidden = k !== st.view; });
      let out = { head: null, anim: null, live: '' };
      if (st.view === 'q') out = this.renderQuestion();
      else if (st.view === 'result') out = this.renderResult(changed);
      else out = { head: this.querySelector('[data-o-qz-start]'), anim: null, live: '' };
      if (!changed) return;
      if (out.anim) { out.anim.classList.remove('is-in'); void out.anim.offsetWidth; out.anim.classList.add('is-in'); }
      if (!this.user) return; // never announce or move focus on load
      this.announce(out.live);
      // Focus follows a user-driven step only, so the page never steals focus on load.
      if (move && out.head) {
        out.head.focus({ preventScroll: true });
        // Bring the stage back into view below the sticky header (--sticky-top), as the design does at 24px.
        const top = this.getBoundingClientRect().top;
        const off = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--sticky-top')) || 24;
        if (top < 0) window.scrollTo({ top: window.scrollY + top - off });
      }
    }

    renderQuestion() {
      const st = this.st; const q = this.questions[st.step];
      const care = this.isCare(); const nm = this.name.trim();
      this.questions.forEach((x, i) => { x.el.hidden = i !== st.step; });
      const head = q.el.querySelector('[data-o-qz-head]');
      const hd = head.dataset;
      head.textContent = !care || !hd.qCare ? hd.q : nm ? fill(hd.qCare, { name: nm }) : (hd.qAnon || hd.q);
      const sel = this.pending || st.answers[q.id];
      q.a.forEach((a) => {
        const b = a.el.dataset;
        a.el.setAttribute('aria-checked', sel === a.id ? 'true' : 'false');
        a.el.querySelector('[data-o-qz-label]').textContent = this.label(a);
        const sub = care && b.subCare ? b.subCare : b.sub;
        const se = a.el.querySelector('[data-o-qz-sub]');
        se.textContent = sub || '';
        se.hidden = !sub;
      });
      const hint = q.el.querySelector('[data-o-qz-hint]');
      if (hint) hint.textContent = fill(this.dataset.hint, { n: q.a.length });
      const nf = q.el.querySelector('[data-o-qz-name]');
      if (nf) {
        nf.hidden = !(care && q.a.some((a) => a.care));
        const input = nf.querySelector('[data-o-qz-name-field]');
        if (input && input.value !== this.name) input.value = this.name;
      }
      const n = this.counted.indexOf(q) + 1; const total = this.counted.length;
      const done = n > 0 ? n - (this.pending ? 0 : 1) : 0;
      if (this.progress) this.progress.hidden = n < 1;
      this.segs.forEach((s, i) => { s.classList.toggle('is-done', i < done); s.classList.toggle('is-now', i === done); });
      const label = n > 0 ? fill(this.dataset.progress, { n, total }) : '';
      if (this.progressLabel) this.progressLabel.textContent = label;
      return { head, anim: q.el, live: label };
    }

    renderResult(changed) {
      const r = score(this.st.answers, this.questions, this.models, this.tie, this.min);
      const primary = this.force || r.primary;
      this.result = r;
      const d = this.dataset; const care = this.isCare(); const nm = this.name.trim();
      const eyebrow = !care ? d.eyebrow : nm ? fill(d.eyebrowName, { name: nm }) : d.eyebrowCare;
      let shown = null;
      this.results.forEach((el, i) => { const on = this.models[i].id === primary; el.hidden = !on; if (on) shown = el; });
      if (!shown) return { head: null, anim: null, live: '' };
      shown.querySelectorAll('[data-o-qz-eyebrow]').forEach((el) => { el.textContent = eyebrow; });
      // The doctor line: "their hearing" when the shopper is helping someone (Jackson, 10/10).
      shown.querySelectorAll('[data-o-qz-note]').forEach((el) => { el.textContent = care && d.noteCare ? d.noteCare : d.note || el.textContent; });
      // "Why this one": each reason under the answer that earned it, then the first-pair line.
      const said = (qid, aid) => {
        const q = this.questions.find((x) => x.id === qid); const a = q && q.a.find((x) => x.id === aid);
        return a ? fill(d.said, { answer: this.label(a) }) : null;
      };
      const rows = primary === r.primary ? r.reasons.map((x) => ({ text: x.text, said: x.said ? said(x.said.q, x.said.a) : null })) : [];
      if (r.firstPair && d.firstPair) {
        const q = this.questions.find((x) => x.a.some((a) => a.firstPair && this.st.answers[x.id] === a.id));
        const a = q && q.a.find((x) => x.firstPair && this.st.answers[q.id] === x.id);
        rows.push({ text: d.firstPair, said: a ? said(q.id, a.id) : null });
      }
      const ul = shown.querySelector('[data-o-qz-why]');
      if (ul) {
        ul.textContent = '';
        rows.forEach((x) => {
          const li = document.createElement('li');
          li.className = 'o-qz-why__row' + (x.said ? ' o-qz-why__row--said' : '');
          if (x.said) { const s = document.createElement('span'); s.className = 'o-qz-why__said'; s.textContent = x.said; li.appendChild(s); }
          if (this.check) li.appendChild(this.check.content.cloneNode(true));
          const t = document.createElement('span'); t.className = 'o-qz-why__text'; t.textContent = x.text; li.appendChild(t);
          ul.appendChild(li);
        });
        ul.parentElement.hidden = !rows.length;
      }
      const ru = primary === r.primary ? r.runnerUp : null;
      let any = false;
      this.rows.forEach((el) => {
        const on = !!ru && el.dataset.model === ru;
        el.hidden = !on;
        if (on) { any = true; const t = el.querySelector('[data-o-qz-row-reason]'); if (t) { t.textContent = r.runnerUpReason || ''; t.hidden = !r.runnerUpReason; } }
      });
      if (this.also) this.also.hidden = !any;
      if (changed && this.user && !this.force) this.emit('quiz_result', { primary: r.primary, runnerUp: r.runnerUp });
      const head = shown.querySelector('[data-o-qz-head]');
      return { head, anim: shown, live: eyebrow ? eyebrow + ': ' + (head ? head.textContent.trim() : '') : '' };
    }

    /* == Events == */
    onClick(e) {
      const t = e.target.closest('[data-o-qz-opt],[data-o-qz-start],[data-o-qz-back],[data-o-qz-next],[data-o-qz-retake],[data-o-qz-view-model],[data-o-qz-row-link]');
      if (!t || !this.contains(t)) return;
      if (t.matches('[data-o-qz-opt]')) this.choose(t.dataset.key);
      else if (t.matches('[data-o-qz-start]')) this.start();
      else if (t.matches('[data-o-qz-back]')) { e.preventDefault(); this.back(); } else if (t.matches('[data-o-qz-next]')) this.next();
      else if (t.matches('[data-o-qz-retake]')) { e.preventDefault(); this.retake(); } else if (t.matches('[data-o-qz-view-model]')) this.emit('quiz_view_model', { model: this.result && this.result.primary, slot: 'primary' });
      else if (t.matches('[data-o-qz-row-link]')) { const row = t.closest('[data-o-qz-row]'); this.emit('quiz_view_model', { model: row && row.dataset.model, slot: 'runner_up' }); }
    }

    // Keys 1 to n pick an answer on desktop while focus is in the stage or on the page body.
    onKey(e) {
      if (this.st.view !== 'q' || !desktop() || this.pending) return;
      if (/INPUT|TEXTAREA|SELECT/.test(e.target.tagName) || e.target.isContentEditable || e.metaKey || e.ctrlKey || e.altKey) return;
      const q = this.questions[this.st.step]; const k = Number(e.key);
      if (!q || !(k >= 1 && k <= q.a.length)) return;
      const el = document.activeElement;
      if (el === document.body || this.contains(el)) this.choose(q.a[k - 1].id);
    }

    // Theme editor: selecting a question or answer block opens that step; a model block opens its result.
    onBlock(e) {
      if (!this.contains(e.target)) return;
      const qi = this.questions.findIndex((q) => q.el === e.target || q.el.contains(e.target));
      clearTimeout(this.timer);
      this.pending = null;
      if (qi >= 0) { this.force = null; this.st.view = 'q'; this.st.step = qi; } else if (e.target.matches('[data-o-qz-res]')) { this.force = e.target.dataset.model; this.st.view = 'result'; }
      this.render(false);
    }

    /* Add to cart through the theme's cart drawer (as the PDP and the old quiz do); without it the form posts. */
    onSubmit(e) {
      const form = e.target.closest('[data-o-qz-buy]');
      if (!form) return;
      this.emit('quiz_add_to_cart', { model: this.result && this.result.primary });
      const drawer = document.querySelector('cart-drawer');
      if (!drawer || typeof drawer.add !== 'function') return;
      e.preventDefault();
      const btn = form.querySelector('[data-o-qz-add]');
      const err = form.parentElement.querySelector('[data-o-qz-error]');
      const id = parseInt(form.querySelector('[name="id"]').value, 10);
      if (!id || (btn && btn.disabled)) return;
      if (btn) { btn.disabled = true; btn.setAttribute('aria-busy', 'true'); }
      if (err) { err.hidden = true; err.textContent = ''; }
      if (typeof drawer.setActiveElement === 'function') drawer.setActiveElement(btn);
      drawer.add([{ id, quantity: 1 }], true).catch((error) => {
        if (err) { err.textContent = (error && error.message) || this.dataset.error || ''; err.hidden = !err.textContent; }
      }).finally(() => { if (btn) { btn.disabled = false; btn.removeAttribute('aria-busy'); } });
    }

    emit(name, detail) {
      const data = Object.assign({ guidance_version: this.dataset.guidanceVersion || 'legacy' }, detail || {});
      this.dispatchEvent(new CustomEvent('oricle:quiz', { bubbles: true, detail: Object.assign({ event: name }, data) }));
      try {
        if (window.Shopify && window.Shopify.analytics && typeof window.Shopify.analytics.publish === 'function') window.Shopify.analytics.publish(name, data);
      } catch (err) { /* analytics never blocks the quiz */ }
    }

    announce(text) {
      if (!this.live) return;
      this.live.textContent = '';
      requestAnimationFrame(() => { this.live.textContent = text || ''; });
    }
  }
  if (!customElements.get('oricle-quiz')) customElements.define('oricle-quiz', OricleQuiz);
})();
